import { toast } from '@heroui/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  Modal,
  NumberField,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const priceRuleSchema = z.object({
  currency: z.string().length(3, '币种须为 3 位 ISO 码'),
  unitPriceYuan: z.coerce.number().min(0, '单价不能为负'),
  priority: z.coerce.number().int().min(0),
});

type PriceRuleFormValues = z.infer<typeof priceRuleSchema>;

export type SkuPriceRuleFormItem = {
  id?: string;
  skuId?: string | null;
  currency?: string | null;
  unitPrice?: number | string | null;
  priority?: number | null;
};

interface SkuPriceRuleEditorModalProps {
  open: boolean;
  skuId: string;
  skuName?: string;
  priceRule?: SkuPriceRuleFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

function fenToYuan(value: number | string | null | undefined): number {
  const n = typeof value === 'string' ? Number(value) : (value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n) / 100;
}

function yuanToFen(yuan: number): number {
  return Math.round(yuan * 100);
}

function buildDefaultValues(rule?: SkuPriceRuleFormItem | null): PriceRuleFormValues {
  if (rule) {
    return {
      currency: rule.currency || 'CNY',
      unitPriceYuan: fenToYuan(rule.unitPrice),
      priority: rule.priority ?? 0,
    };
  }
  return {
    currency: 'CNY',
    unitPriceYuan: 0,
    priority: 0,
  };
}

export function SkuPriceRuleEditorModal({
  open,
  skuId,
  skuName,
  priceRule,
  onCancel,
  onSuccess,
}: SkuPriceRuleEditorModalProps) {
  const utils = trpc.useUtils();
  const isUpdate = Boolean(priceRule?.id);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<PriceRuleFormValues>({
    resolver: zodResolver(priceRuleSchema) as any,
    defaultValues: buildDefaultValues(priceRule),
    mode: 'onChange',
  });
  const { reset, control } = form;
  const errors = form.formState.errors;

  useEffect(() => {
    if (!open) {
      reset(buildDefaultValues());
      return;
    }
    reset(buildDefaultValues(priceRule));
  }, [open, priceRule, reset]);

  const createMutation = trpc.bizProduct.skuPriceRule.create.useMutation({
    onSuccess: () => {
      toast.success('定价规则已创建');
      form.reset();
      onSuccess();
      void utils.bizProduct.sku.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('创建失败', { description: error.message });
    },
  });

  const updateMutation = trpc.bizProduct.skuPriceRule.update.useMutation({
    onSuccess: () => {
      toast.success('定价规则已更新');
      form.reset();
      onSuccess();
      void utils.bizProduct.sku.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('更新失败', { description: error.message });
    },
  });

  const onSubmit = async (values: PriceRuleFormValues) => {
    const payload = {
      currency: values.currency.toUpperCase(),
      unitPrice: yuanToFen(values.unitPriceYuan),
      priority: values.priority,
    };

    if (isUpdate && priceRule?.id) {
      await updateMutation.mutateAsync({ id: priceRule.id, data: payload });
    } else {
      await createMutation.mutateAsync({ skuId, ...payload });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const title = useMemo(
    () => (isUpdate ? '编辑定价规则' : '新建定价规则'),
    [isUpdate]
  );

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg" className="max-w-2xl">
          <Modal.Dialog
            key={open ? (isUpdate && priceRule?.id ? `edit-${priceRule.id}` : 'create') : 'closed'}
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">{title}</Modal.Heading>
              <Description className="text-sm">
                {skuName ? `SKU：${skuName}` : '配置 SKU 报价规则'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4">
              <form
                id="editor-sku-price-rule-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Controller
                    control={control}
                    name="currency"
                    render={({ field }) => (
                      <TextField className="w-full" isInvalid={!!errors.currency}>
                        <Label className="text-sm font-medium">
                          币种 <span className="text-danger">*</span>
                        </Label>
                        <Input
                          maxLength={3}
                          placeholder="CNY"
                          variant="secondary"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                        {errors.currency ? (
                          <FieldError>{errors.currency.message}</FieldError>
                        ) : null}
                      </TextField>
                    )}
                  />

                  <Controller
                    control={control}
                    name="unitPriceYuan"
                    render={({ field }) => (
                      <NumberField
                        className="w-full"
                        variant="secondary"
                        isInvalid={!!errors.unitPriceYuan}
                        minValue={0}
                        formatOptions={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
                        value={field.value}
                        onChange={(v) => field.onChange(v ?? 0)}
                      >
                        <Label className="text-sm font-medium">
                          单价（元） <span className="text-danger">*</span>
                        </Label>
                        <NumberField.Group>
                          <NumberField.DecrementButton />
                          <NumberField.Input className="w-full" />
                          <NumberField.IncrementButton />
                        </NumberField.Group>
                        <Description className="text-xs">保存时自动换算为分存库</Description>
                        {errors.unitPriceYuan ? (
                          <FieldError>{errors.unitPriceYuan.message}</FieldError>
                        ) : null}
                      </NumberField>
                    )}
                  />
                </div>

                <Controller
                  control={control}
                  name="priority"
                  render={({ field }) => (
                    <NumberField
                      className="w-full"
                      variant="secondary"
                      isInvalid={!!errors.priority}
                      minValue={0}
                      value={field.value}
                      onChange={(v) => field.onChange(v ?? 0)}
                    >
                      <Label className="text-sm font-medium">优先级</Label>
                      <NumberField.Group>
                        <NumberField.DecrementButton />
                        <NumberField.Input className="w-full" />
                        <NumberField.IncrementButton />
                      </NumberField.Group>
                      <Description className="text-xs">多规则时值小优先</Description>
                      {errors.priority ? (
                        <FieldError>{errors.priority.message}</FieldError>
                      ) : null}
                    </NumberField>
                  )}
                />
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '保存中…' : '保存'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
